import React from 'react';

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ApartmentIcon from '@mui/icons-material/Apartment';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CalculateIcon from '@mui/icons-material/Calculate';
import ForestIcon from '@mui/icons-material/Forest';
import GavelIcon from '@mui/icons-material/Gavel';
import HomeIcon from '@mui/icons-material/Home';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { Box, Divider, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

export interface InsightArticle {
  id: string;
  slug: string;
  category: string;
  audience: string;
  readTime: string;
  title: string;
  summary: string;
  icon: React.ReactNode;
  tags: string[];
  content: React.ReactNode;
}

/** Serialisable subset — safe to pass through getStaticProps */
export interface InsightArticleMeta {
  id: string;
  slug: string;
  category: string;
  audience: string;
  readTime: string;
  title: string;
  summary: string;
  tags: string[];
}

// ─── Shared typography helpers ────────────────────────────────────────────────

const H2 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="h6" gutterBottom sx={{ mt: 3, fontWeight: 700 }}>
    {children}
  </Typography>
);

const H3 = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 700 }}>
    {children}
  </Typography>
);

const P = ({ children }: { children: React.ReactNode }) => (
  <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
    {children}
  </Typography>
);

const UL = ({ children }: { children: React.ReactNode }) => (
  <Box component="ul" sx={{ pl: 3, mb: 2 }}>
    {children}
  </Box>
);

const OL = ({ children }: { children: React.ReactNode }) => (
  <Box component="ol" sx={{ pl: 3, mb: 2 }}>
    {children}
  </Box>
);

const LI = ({ children }: { children: React.ReactNode }) => (
  <li>
    <Typography variant="body1" sx={{ lineHeight: 1.8, mb: 0.5 }}>
      {children}
    </Typography>
  </li>
);

const Note = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      border: '1px solid',
      borderColor: 'warning.light',
      bgcolor: 'warning.50',
      borderRadius: 1,
      px: 2,
      py: 1.5,
      mb: 2,
    }}
  >
    <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
      <strong>Important:</strong> {children}
    </Typography>
  </Box>
);

// ─── Articles ─────────────────────────────────────────────────────────────────

export const INSIGHT_ARTICLES: InsightArticle[] = [
  // ── 1: Buyer's Guide ──────────────────────────────────────────────────────
  {
    id: '1',
    slug: 'buying-property-south-india-guide',
    category: "Buyer's Guide",
    audience: 'Buyers',
    readTime: '8 min read',
    title: 'Complete Step-by-Step Guide to Buying Property in South India',
    summary:
      'From shortlisting to registration — a comprehensive, jargon-free walkthrough of every stage involved in purchasing residential or agricultural property across Tamil Nadu, Andhra Pradesh, Telangana, Karnataka, and Kerala.',
    icon: <HomeIcon />,
    tags: ["Buyer's Guide", 'Property Registration', 'Due Diligence', 'South India'],
    content: (
      <>
        <P>
          Buying property in South India involves several legally mandated steps, each with its own documentation, timelines, and government offices. This guide walks you through the entire process — from identifying a property to receiving the registered title deed.
        </P>

        <Note>
          Laws and fees vary by state and are subject to revision. Always verify current rates and procedures with the relevant Sub-Registrar's office or a licensed advocate before proceeding.
        </Note>

        <H2>Step 1 — Shortlist and Physically Verify the Property</H2>
        <P>
          Visit the site personally. Verify that the physical boundaries match the dimensions stated in the title documents. Check road access, drainage, proximity to utilities, and any visible encroachments. Confirm the survey number on the property corresponds to the survey number in the documents — discrepancies are common and must be resolved before proceeding.
        </P>

        <H2>Step 2 — Obtain and Verify the Encumbrance Certificate (EC)</H2>
        <P>
          The EC is the single most important document in Indian property due diligence. It is issued by the Sub-Registrar's office and lists all registered transactions — sales, mortgages, gifts, or legal disputes — linked to the property for a specified period.
        </P>
        <UL>
          <LI>Request an EC for a minimum of 13 years (30 years is standard for bank-financed purchases).</LI>
          <LI>The EC must be clean — free of any registered mortgages, court attachments, or undischarged loans.</LI>
          <LI>In Tamil Nadu, apply via the TNIGR (Inspector General of Registration) online portal or directly at the Sub-Registrar's office.</LI>
          <LI>In Andhra Pradesh and Telangana, ECs can be obtained online via the IGRS portal.</LI>
        </UL>

        <H2>Step 3 — Verify Revenue Records</H2>
        <P>Revenue records confirm the land's current ownership and land-use classification. The key documents vary by state:</P>
        <H3>Tamil Nadu</H3>
        <UL>
          <LI><strong>Patta</strong> — Record of Rights issued by the Tahsildar, confirming who has registered ownership of the land.</LI>
          <LI><strong>Chitta</strong> — Classification of land (wet/dry/poromboke/building site).</LI>
          <LI><strong>Adangal / Pahani</strong> — Crop cultivation register for agricultural land.</LI>
          <LI><strong>A-Register extract</strong> — Survey-number-wise details of the land.</LI>
        </UL>
        <H3>Karnataka</H3>
        <UL>
          <LI><strong>RTC (Record of Rights, Tenancy and Crops) / Pahani</strong> — Equivalent to Patta; available via the Bhoomi portal.</LI>
          <LI><strong>Mutation Register (MR) extract</strong> — Records changes in ownership.</LI>
        </UL>
        <H3>Andhra Pradesh and Telangana</H3>
        <UL>
          <LI><strong>Pahani / Adangal</strong> — Land revenue record confirming ownership, area, and crop details.</LI>
          <LI>In Telangana, verify via the <strong>Dharani portal</strong> (dharani.telangana.gov.in) for agricultural and non-agricultural lands.</LI>
        </UL>
        <H3>Kerala</H3>
        <UL>
          <LI><strong>Thandaper (Property Card)</strong> — Issued by the Revenue Department confirming ownership and land classification.</LI>
        </UL>

        <H2>Step 4 — Verify Layout and Building Approvals</H2>
        <P>
          Ensure the plot is part of an approved layout (not unauthorised) from the relevant planning authority. Purchasing an unapproved plot is illegal and banks will not finance it:
        </P>
        <UL>
          <LI><strong>Chennai and surrounding areas:</strong> CMDA (Chennai Metropolitan Development Authority) approval required.</LI>
          <LI><strong>Tamil Nadu outside Chennai metro:</strong> DTCP (Directorate of Town and Country Planning) approval.</LI>
          <LI><strong>Hyderabad metro:</strong> HMDA (Hyderabad Metropolitan Development Authority) or GHMC sanction.</LI>
          <LI><strong>Bengaluru:</strong> BDA (Bangalore Development Authority) layout approval or BBMP building plan sanction.</LI>
          <LI><strong>Other AP cities:</strong> Municipal Corporation or VGTM UDA as applicable.</LI>
        </UL>

        <H2>Step 5 — Execute a Sale Agreement</H2>
        <P>
          A Sale Agreement (Agreement to Sell) is a written contract between buyer and seller establishing the price, payment schedule, possession date, and conditions of the sale. Key points:
        </P>
        <UL>
          <LI>Always get the Sale Agreement drafted by a licensed advocate.</LI>
          <LI>Stamp duty is payable on the Sale Agreement (amounts vary by state).</LI>
          <LI>A typical advance / token amount of 10–20% of the total consideration is paid at this stage.</LI>
          <LI>Include penalty clauses for both parties for delay or default.</LI>
          <LI>The Sale Agreement is not the same as the Sale Deed — title does not transfer at this stage.</LI>
        </UL>

        <H2>Step 6 — Pay Stamp Duty and Register the Sale Deed</H2>
        <P>
          The Sale Deed is the document that legally transfers title from seller to buyer. It must be registered at the Sub-Registrar's office having jurisdiction over the property's location.
        </P>
        <UL>
          <LI>Both buyer and seller (or their authorised representatives with a registered Power of Attorney) must be present.</LI>
          <LI>Two witnesses are required at registration.</LI>
          <LI>Original title documents, EC, and identity proofs must be produced.</LI>
          <LI>Stamp duty and registration charges are paid at this stage (rates vary by state — see our Stamp Duty article).</LI>
          <LI>The original registered Sale Deed is returned to the buyer, typically within a few working days to a few weeks depending on the Sub-Registrar's office.</LI>
        </UL>

        <H2>Step 7 — Mutation / Patta Transfer</H2>
        <P>
          After registration, the buyer must apply for mutation — the update of the government revenue records to reflect the new ownership. This is a separate process from registration:
        </P>
        <UL>
          <LI><strong>Tamil Nadu:</strong> Apply for Patta transfer at the Tahsildar's office by submitting the registered Sale Deed, old Patta, and a mutation application.</LI>
          <LI><strong>Karnataka:</strong> Apply for Khata transfer at the BBMP/municipal authority (for urban property) or at the Tahsildar's office for revenue records.</LI>
          <LI><strong>AP / Telangana:</strong> Mutation at the Revenue Divisional Officer (RDO) or Tahsildar; in Telangana, agricultural land mutations can be tracked via Dharani portal.</LI>
        </UL>
        <P>
          Mutation does not confer title — it is an administrative record. The title to the property is confirmed by the registered Sale Deed. However, mutation is essential for payment of property taxes, future sale, and mortgage.
        </P>

        <H2>Summary Checklist for Buyers</H2>
        <OL>
          <LI>Physical site visit and boundary verification</LI>
          <LI>EC for minimum 13 years — confirmed clear</LI>
          <LI>Revenue records (Patta/RTC/Pahani) — verified in seller's name</LI>
          <LI>Layout / planning approval confirmed from relevant authority</LI>
          <LI>No court cases, attachments, or injunctions on the property</LI>
          <LI>Property tax receipts up to date</LI>
          <LI>Sale Agreement executed with advocate</LI>
          <LI>Home loan sanction (if applicable)</LI>
          <LI>Sale Deed registered at Sub-Registrar's office</LI>
          <LI>Patta / Khata / Mutation applied for</LI>
        </OL>
      </>
    ),
  },

  // ── 2: Seller's Guide ─────────────────────────────────────────────────────
  {
    id: '2',
    slug: 'selling-property-south-india-guide',
    category: "Seller's Guide",
    audience: 'Sellers',
    readTime: '6 min read',
    title: "Seller's Complete Guide: How to Sell Property in South India",
    summary:
      'A practical guide for property sellers — from gathering your documents and pricing your property correctly, to understanding capital gains tax obligations and completing registration.',
    icon: <PersonSearchIcon />,
    tags: ["Seller's Guide", 'Capital Gains Tax', 'Property Pricing', 'Legal Documents'],
    content: (
      <>
        <P>
          Selling property in India involves more preparation than most sellers anticipate. Incomplete documents are the primary reason property sales fall through at the last stage. This guide helps sellers prepare thoroughly, price correctly, and close without legal complications.
        </P>

        <Note>
          Tax laws are subject to change with each Union Budget. Consult a Chartered Accountant for your specific capital gains tax liability before concluding a sale.
        </Note>

        <H2>Step 1 — Assemble Your Complete Document Set</H2>
        <P>A buyer's advocate and any bank financing the buyer's purchase will scrutinise every document. Prepare the following in advance:</P>
        <UL>
          <LI><strong>Original title deed chain</strong> — All Sale Deeds going back at least 30 years establishing an unbroken chain of title.</LI>
          <LI><strong>EC (Encumbrance Certificate)</strong> — For a minimum of 13 years, confirming no registered mortgages or encumbrances.</LI>
          <LI><strong>Patta / RTC / Pahani</strong> (as applicable to your state) — In your name, matching the survey number in the title deed.</LI>
          <LI><strong>Layout approval</strong> — DTCP/CMDA/BDA/HMDA sanction as applicable. Original layout plan.</LI>
          <LI><strong>Property tax receipts</strong> — All payments current; no arrears outstanding.</LI>
          <LI><strong>NOC from housing society or apartment association</strong> — If selling a flat or a plot in a gated community.</LI>
          <LI><strong>Building plan sanction and Completion Certificate / OC</strong> — If selling a constructed house or flat.</LI>
          <LI><strong>Discharge certificate from bank</strong> — If there was a home loan on the property that has been repaid; this confirms the mortgage is released.</LI>
        </UL>

        <H2>Step 2 — Determine the Correct Sale Price</H2>
        <P>
          Indian property transactions involve two values: the <strong>guideline value</strong> (also called circle rate, ready reckoner rate, or stamp duty value) published by the state government, and the <strong>market value</strong> mutually agreed between buyer and seller. Note:
        </P>
        <UL>
          <LI>Stamp duty is calculated on the <strong>higher</strong> of guideline value or agreed sale price.</LI>
          <LI>Sellers cannot register a property at a price significantly below guideline value — the Sub-Registrar will reject or revise the registration value.</LI>
          <LI>For income tax purposes, if the sale consideration is less than the stamp duty value by more than the prescribed threshold, the stamp duty value is treated as the full value of consideration for capital gains calculation.</LI>
        </UL>

        <H2>Step 3 — Understand Your Capital Gains Tax Liability</H2>
        <P>
          Profit from selling property is taxable as capital gains. The classification depends on the holding period:
        </P>
        <H3>Long-Term Capital Gains (LTCG) — Holding period more than 24 months</H3>
        <UL>
          <LI>For properties acquired before 23 July 2024: Sellers can choose between (a) 20% tax with Cost Inflation Index (indexation benefit) or (b) 12.5% tax without indexation — whichever is more beneficial.</LI>
          <LI>For properties acquired on or after 23 July 2024: LTCG is taxed at 12.5% <strong>without</strong> indexation benefit.</LI>
        </UL>
        <H3>Short-Term Capital Gains (STCG) — Holding period 24 months or less</H3>
        <UL>
          <LI>Taxed at the seller's applicable income tax slab rate.</LI>
        </UL>
        <H3>Key LTCG Exemptions for Sellers</H3>
        <UL>
          <LI><strong>Section 54</strong> — Exemption if LTCG is reinvested in one new residential property within 1 year before, or 2 years after, the date of sale (or 3 years if constructing). Applicable only for residential house sellers reinvesting in residential property.</LI>
          <LI><strong>Section 54F</strong> — Exemption for LTCG from sale of any asset (other than a residential house) if the net sale proceeds are invested in a new residential property within 1 year before or 2 years after the sale.</LI>
          <LI><strong>Section 54EC</strong> — Exemption by investing LTCG (up to ₹50 lakh) in specified bonds (NHAI, REC, PFCL, IRFC) within 6 months of the sale. Lock-in period is 5 years.</LI>
        </UL>
        <P>
          The Capital Gains Account Scheme (CGAS) allows sellers to park the reinvestment amount in a designated bank account if the new property purchase/construction is not completed before the income tax return filing deadline.
        </P>

        <H2>Step 4 — TDS on Property Sale</H2>
        <P>
          Under Section 194-IA of the Income Tax Act, the buyer is required to deduct TDS at <strong>1% of the total sale consideration</strong> if the consideration is ₹50 lakh or more. The buyer deposits this TDS and provides Form 16B to the seller. The seller claims this TDS credit in their income tax return. This is the buyer's obligation, but sellers must be aware of it and factor it into negotiations.
        </P>

        <H2>Step 5 — Execute Sale Agreement and Sale Deed</H2>
        <P>
          Once buyer and seller agree on price and terms, a Sale Agreement is executed. After all conditions are met (payment schedule, encumbrance clearance, loan sanction), the Sale Deed is registered at the Sub-Registrar's office. Both parties — or their duly empowered Power of Attorney holders — must be present at registration.
        </P>
        <P>
          Sellers typically receive the balance payment (after advance and any home loan disbursement to the seller) at or before registration. Do not hand over original documents until full payment and registration are complete.
        </P>

        <H2>Practical Tips for South Indian Sellers</H2>
        <UL>
          <LI>Get your EC and Patta in order <strong>before</strong> listing the property — buyers and their banks will ask for these immediately.</LI>
          <LI>Clear any property tax arrears before listing to avoid last-minute complications.</LI>
          <LI>If the title deed is in the name of a deceased family member, complete the legal heir/succession process and update the Patta before listing.</LI>
          <LI>Use a verified platform like FindMyProps to reach genuine, pre-verified buyers and avoid wasting time on non-serious inquiries.</LI>
        </UL>
      </>
    ),
  },

  // ── 3: RERA ───────────────────────────────────────────────────────────────
  {
    id: '3',
    slug: 'rera-south-india-state-guide',
    category: 'Regulations',
    audience: 'Buyers & Agents',
    readTime: '7 min read',
    title: 'RERA in South India: A Complete State-by-State Guide',
    summary:
      'How the Real Estate (Regulation and Development) Act, 2016 protects buyers and regulates developers and agents across Tamil Nadu, Andhra Pradesh, Telangana, Karnataka, and Kerala.',
    icon: <GavelIcon />,
    tags: ['RERA', 'TNRERA', 'APRERA', 'K-RERA', 'TGRERA', 'Buyer Protection'],
    content: (
      <>
        <P>
          The Real Estate (Regulation and Development) Act, 2016 (RERA) is a central legislation enacted to protect homebuyers, bring accountability to real estate developers, and establish a structured dispute resolution mechanism. Each state has constituted its own regulatory authority to implement RERA.
        </P>

        <Note>
          RERA applies primarily to projects involving construction for sale. Pure land (plot) sales in approved layouts may or may not require RERA registration depending on project size and the specific state's rules. Always verify with the respective state RERA authority.
        </Note>

        <H2>When Does RERA Apply?</H2>
        <P>Under Section 3 of the RERA Act, a real estate project must be registered with the respective state RERA authority <strong>before</strong> any advertisement, marketing, or sale booking, if:</P>
        <UL>
          <LI>The plot of land proposed to be developed exceeds <strong>500 square metres</strong>, OR</LI>
          <LI>The number of apartments or units proposed to be developed exceeds <strong>eight</strong>, OR</LI>
          <LI>The project involves development of plots for sale exceeding the above thresholds.</LI>
        </UL>
        <P>Renovation or repair projects that do not involve marketing, advertising, or new allotments are exempt from RERA registration.</P>

        <H2>Key Rights RERA Gives Buyers</H2>
        <UL>
          <LI><strong>Right to accurate information:</strong> Developers must disclose approved plans, layout, status of statutory approvals, completion timeline, and list of booked units on the RERA portal.</LI>
          <LI><strong>Escrow protection:</strong> Developers must deposit 70% of the amount collected from buyers into a separate designated bank account (escrow), which can only be withdrawn for construction-related expenses on that specific project.</LI>
          <LI><strong>No unauthorised changes:</strong> Developers cannot make changes to the sanctioned plans without the written consent of at least two-thirds of the buyers.</LI>
          <LI><strong>Structural defect liability:</strong> Developer is liable for structural defects reported within <strong>five years</strong> of possession for a period of up to five years.</LI>
          <LI><strong>Compensation for delays:</strong> If a developer fails to hand over possession on the committed date, the buyer is entitled to interest on the amount paid, or may withdraw from the project and receive a full refund with interest.</LI>
          <LI><strong>Right to documents:</strong> Buyers are entitled to receive all documents and information they are entitled to under the agreement at the time of booking.</LI>
        </UL>

        <H2>State-by-State RERA Authorities</H2>
        <Divider sx={{ my: 2 }} />

        <H3>Tamil Nadu — TNRERA</H3>
        <UL>
          <LI><strong>Authority:</strong> Tamil Nadu Real Estate Regulatory Authority (TNRERA)</LI>
          <LI><strong>Website:</strong> rera.tn.gov.in</LI>
          <LI><strong>Jurisdiction:</strong> All real estate projects across Tamil Nadu meeting the registration threshold.</LI>
          <LI>Buyers can verify if a project is registered, check its completion status, and file complaints on the TNRERA portal.</LI>
          <LI>Agents must register with TNRERA; registration number must be displayed on all marketing materials.</LI>
        </UL>

        <H3>Andhra Pradesh — APRERA</H3>
        <UL>
          <LI><strong>Authority:</strong> Andhra Pradesh Real Estate Regulatory Authority (APRERA)</LI>
          <LI><strong>Website:</strong> rera.ap.gov.in</LI>
          <LI>AP adopted its own rules under the central RERA Act. Projects in the Amaravati capital region are subject to APRERA oversight.</LI>
        </UL>

        <H3>Telangana — TGRERA</H3>
        <UL>
          <LI><strong>Authority:</strong> Telangana Real Estate Regulatory Authority (TGRERA)</LI>
          <LI><strong>Website:</strong> rera.telangana.gov.in</LI>
          <LI>One of the more active RERA authorities in South India, with a large number of registered projects in the Hyderabad metropolitan region.</LI>
        </UL>

        <H3>Karnataka — K-RERA</H3>
        <UL>
          <LI><strong>Authority:</strong> Karnataka Real Estate Regulatory Authority (K-RERA)</LI>
          <LI><strong>Website:</strong> rera.karnataka.gov.in</LI>
          <LI>Covers the large Bengaluru real estate market. Buyers purchasing apartments or plotted developments in Bengaluru should always verify RERA registration.</LI>
        </UL>

        <H3>Kerala — RERA Kerala</H3>
        <UL>
          <LI><strong>Authority:</strong> Kerala Real Estate Regulatory Authority</LI>
          <LI><strong>Website:</strong> rera.kerala.gov.in</LI>
          <LI>Kerala has seen slower uptake compared to other South Indian states, but the authority is operational and accepting project registrations and complaints.</LI>
        </UL>

        <H2>RERA Obligations for Real Estate Agents</H2>
        <UL>
          <LI>Every real estate agent must register with the state RERA authority in which they practice before facilitating any transaction involving a RERA-registered project.</LI>
          <LI>The RERA registration certificate must be renewed periodically (renewal cycles vary by state — typically annual).</LI>
          <LI>Agents must not represent or facilitate sale of a project that is not RERA-registered where registration is mandatory.</LI>
          <LI>Penalty for agents operating without RERA registration: up to ₹10,000 per day of default, potentially extending to imprisonment for continued violations.</LI>
          <LI>Agents must maintain records of every transaction for a period of 5 years.</LI>
        </UL>

        <H2>How to File a RERA Complaint</H2>
        <OL>
          <LI>Visit the respective state RERA portal and locate the complaint filing section.</LI>
          <LI>Register or log in with your credentials.</LI>
          <LI>Fill the complaint form providing project RERA registration number, nature of grievance, and supporting documents.</LI>
          <LI>Pay the prescribed complaint filing fee (varies by state and nature of complaint).</LI>
          <LI>The Adjudicating Officer hears the complaint; orders for interest or compensation are enforceable as civil court decrees.</LI>
        </OL>

        <H2>Verifying a Project Before Buying</H2>
        <P>
          Before paying any advance for an under-construction project, visit the respective state RERA portal and search for the project by name, promoter name, or RERA registration number. A legitimate, registered project will show:
        </P>
        <UL>
          <LI>Registered project details and completion date</LI>
          <LI>Approved project layout and plans</LI>
          <LI>Total units, units sold, and units available</LI>
          <LI>Designated account bank details</LI>
          <LI>Quarterly progress reports filed by the promoter</LI>
        </UL>
      </>
    ),
  },

  // ── 4: GST ────────────────────────────────────────────────────────────────
  {
    id: '4',
    slug: 'gst-real-estate-south-india',
    category: 'Taxation',
    audience: 'Buyers',
    readTime: '6 min read',
    title: 'GST on Real Estate in South India: What Every Buyer Must Know',
    summary:
      'A clear breakdown of Goods and Services Tax on residential and commercial property — when it applies, when it does not, and how affordable housing is taxed differently.',
    icon: <CalculateIcon />,
    tags: ['GST', 'Affordable Housing', 'Under Construction', 'Tax'],
    content: (
      <>
        <P>
          Goods and Services Tax (GST) has significantly changed real estate taxation since its introduction in 2017. The rates were revised further in 2019 by the GST Council. Understanding when GST applies — and at what rate — is essential before signing any property purchase agreement.
        </P>

        <Note>
          GST rates and definitions are subject to revision by the GST Council. Always verify the applicable rate with a GST practitioner or Chartered Accountant for your specific transaction.
        </Note>

        <H2>The Fundamental Rule: Ready-to-Move vs Under Construction</H2>
        <P>
          The most important distinction in real estate GST is whether the property has received its <strong>Occupancy Certificate (OC) or Completion Certificate</strong> at the time of the agreement:
        </P>
        <UL>
          <LI><strong>Ready-to-move-in property (OC already received):</strong> <strong>No GST is payable.</strong> The sale is treated as a sale of immovable property, not a supply of services, and is therefore outside the scope of GST.</LI>
          <LI><strong>Under-construction property (OC not yet received):</strong> GST is applicable. The transaction is treated as a composite supply of construction services.</LI>
          <LI><strong>Pure land purchase:</strong> No GST. Sale of land is not a supply under GST. Stamp duty and registration charges apply.</LI>
        </UL>

        <H2>Current GST Rates for Residential Property (Post-2019 Revision)</H2>
        <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><strong>Property Type</strong></TableCell>
              <TableCell><strong>GST Rate</strong></TableCell>
              <TableCell><strong>Input Tax Credit (ITC)</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Affordable housing — under construction</TableCell>
              <TableCell><strong>1%</strong></TableCell>
              <TableCell>Not available to developer</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Non-affordable residential — under construction</TableCell>
              <TableCell><strong>5%</strong></TableCell>
              <TableCell>Not available to developer</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Residential — ready to move (OC received)</TableCell>
              <TableCell><strong>Nil (No GST)</strong></TableCell>
              <TableCell>N/A</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Commercial property — under construction</TableCell>
              <TableCell><strong>12%</strong></TableCell>
              <TableCell>Available to buyer (if registered)</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Pure land purchase</TableCell>
              <TableCell><strong>Nil (No GST)</strong></TableCell>
              <TableCell>N/A</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <H2>What is Affordable Housing for GST Purposes?</H2>
        <P>
          A residential unit qualifies as "affordable housing" for GST purposes if <strong>both</strong> of the following conditions are met:
        </P>
        <UL>
          <LI><strong>Carpet area:</strong>
            <UL>
              <LI>Up to <strong>60 square metres</strong> in metropolitan cities: Bengaluru, Chennai, Delhi NCR (Delhi, Noida, Greater Noida, Ghaziabad, Gurugram, Faridabad), Hyderabad, Kolkata, and Mumbai Metropolitan Region.</LI>
              <LI>Up to <strong>90 square metres</strong> in cities and towns other than those listed above (including Coimbatore, Madurai, Tiruppur, Visakhapatnam, Vijayawada, etc.).</LI>
            </UL>
          </LI>
          <LI><strong>Sale price:</strong> The gross amount charged (excluding GST) does not exceed <strong>₹45 lakh</strong>.</LI>
        </UL>
        <P>
          <strong>Both</strong> conditions must be satisfied simultaneously. A unit with carpet area of 55 sqm but priced at ₹60 lakh does <strong>not</strong> qualify for the 1% rate — it would attract GST at 5%.
        </P>

        <H2>GST on Land Plus Construction (Composite Deals)</H2>
        <P>
          When a developer sells a plot along with a construction agreement (a common structure for villas and row houses), GST is calculated on the construction portion only, not the land. The GST Council has prescribed a deemed land deduction: <strong>one-third of the total amount charged</strong> is treated as the value of land and is excluded from the GST computation. GST applies to the remaining two-thirds.
        </P>
        <P>
          Example: If a developer charges ₹90 lakh for a villa (inclusive of land), the deemed land value is ₹30 lakh (one-third). GST applies to ₹60 lakh. At 5% for non-affordable housing, the GST payable is ₹3 lakh.
        </P>

        <H2>Who Pays GST?</H2>
        <P>
          GST is payable by the <strong>buyer</strong> on under-construction purchases. The developer collects it and remits it to the government. The buyer should receive a proper GST invoice from the developer and verify that the developer's GSTIN (GST Identification Number) is valid on the GST portal (gst.gov.in).
        </P>

        <H2>Practical Implications for South Indian Buyers</H2>
        <UL>
          <LI>In cities like Coimbatore, Madurai, Tiruppur, Nellore, and Guntur (non-metro), buyers have a higher carpet area threshold (90 sqm) for the affordable housing 1% rate — this is a significant advantage compared to metro buyers.</LI>
          <LI>Ready-to-move homes attract no GST, which can make them substantially more cost-effective than under-construction alternatives for comparable properties.</LI>
          <LI>Always ask the developer for the breakup of total price into land value, construction value, and GST. A developer who cannot provide this clearly should be treated as a red flag.</LI>
          <LI>Pure plot purchases (without construction) are not subject to GST — only stamp duty and registration charges apply.</LI>
        </UL>
      </>
    ),
  },

  // ── 5: PMAY ───────────────────────────────────────────────────────────────
  {
    id: '5',
    slug: 'pmay-south-india-homebuyers-guide',
    category: 'Government Schemes',
    audience: 'Buyers',
    readTime: '6 min read',
    title: 'PMAY: How South Indian Homebuyers Can Benefit from the Housing Scheme',
    summary:
      'Eligibility categories, income limits, subsidy amounts, and how to apply for the Pradhan Mantri Awas Yojana housing scheme as a buyer in Tamil Nadu, Andhra Pradesh, Telangana, Karnataka, or Kerala.',
    icon: <AccountBalanceIcon />,
    tags: ['PMAY', 'Housing Subsidy', 'CLSS', 'Affordable Housing', 'Government Scheme'],
    content: (
      <>
        <P>
          The Pradhan Mantri Awas Yojana (PMAY) — Housing for All — is India's flagship urban and rural housing scheme. For eligible buyers, it provides an interest subsidy on home loans through the Credit Linked Subsidy Scheme (CLSS), reducing the effective cost of borrowing significantly.
        </P>

        <Note>
          PMAY has been revised multiple times. Verify the current status of subsidy availability and applicable categories with your lender or at the official PMAY-Urban portal (pmaymis.gov.in) before applying. CLSS benefits for MIG categories under the original PMAY-U scheme ended on 31 March 2021.
        </Note>

        <H2>PMAY-Urban vs PMAY-Gramin</H2>
        <UL>
          <LI><strong>PMAY-Urban (PMAY-U):</strong> For residents of statutory towns and cities. Administered through Urban Local Bodies. Relevant for buyers purchasing homes in Chennai, Coimbatore, Hyderabad, Bengaluru, Thiruvananthapuram, and other urban agglomerations.</LI>
          <LI><strong>PMAY-Gramin (PMAY-G):</strong> For rural areas. Provides financial assistance directly to beneficiaries for constructing or upgrading a pucca house. Administered by the Ministry of Rural Development through state governments.</LI>
        </UL>

        <H2>Credit Linked Subsidy Scheme (CLSS) — Income Categories</H2>
        <P>
          The CLSS provides an upfront interest subsidy credited to the loan account, reducing the principal outstanding and thus lowering EMIs over the tenure. The subsidy categories under the original PMAY-U framework are:
        </P>
        <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Annual Household Income</strong></TableCell>
              <TableCell><strong>Subsidy Rate</strong></TableCell>
              <TableCell><strong>Max Loan Amount for Subsidy</strong></TableCell>
              <TableCell><strong>Max Carpet Area</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>EWS (Economically Weaker Section)</TableCell>
              <TableCell>Up to ₹3 lakh</TableCell>
              <TableCell>6.5%</TableCell>
              <TableCell>₹6 lakh</TableCell>
              <TableCell>30 sqm</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>LIG (Lower Income Group)</TableCell>
              <TableCell>₹3 lakh – ₹6 lakh</TableCell>
              <TableCell>6.5%</TableCell>
              <TableCell>₹6 lakh</TableCell>
              <TableCell>60 sqm</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>MIG-I (Middle Income Group I)</TableCell>
              <TableCell>₹6 lakh – ₹12 lakh</TableCell>
              <TableCell>4%</TableCell>
              <TableCell>₹9 lakh</TableCell>
              <TableCell>160 sqm</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>MIG-II (Middle Income Group II)</TableCell>
              <TableCell>₹12 lakh – ₹18 lakh</TableCell>
              <TableCell>3%</TableCell>
              <TableCell>₹12 lakh</TableCell>
              <TableCell>200 sqm</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <P>
          The subsidy is calculated at Net Present Value (NPV) at a discount rate of 9%, computed over a maximum tenure of 20 years. The NPV amount is credited upfront to the loan account, reducing the outstanding principal immediately.
        </P>

        <H2>Key Eligibility Conditions</H2>
        <UL>
          <LI>The beneficiary household (including spouse and dependent children) should <strong>not own a pucca house</strong> anywhere in India.</LI>
          <LI>For EWS and LIG categories, the property must be in the name of the female head of household, or jointly with her. This is a mandatory condition.</LI>
          <LI>The beneficiary should be a <strong>first-time homebuyer</strong> — no prior PMAY benefit should have been availed.</LI>
          <LI>The loan must be taken from a PMAY-approved lending institution: scheduled commercial banks, housing finance companies (HFCs), regional rural banks (RRBs), state cooperative banks, or small finance banks.</LI>
          <LI>Aadhar is mandatory for identification in PMAY applications.</LI>
        </UL>

        <H2>How to Apply for PMAY-Urban CLSS</H2>
        <OL>
          <LI>Approach any PMAY-approved bank or Housing Finance Company (most major banks and HFCs like HDFC, LIC Housing Finance, NHB-approved lenders participate).</LI>
          <LI>Inform the lender that you wish to avail PMAY-CLSS benefit at the time of applying for the home loan.</LI>
          <LI>Submit the required documents including Aadhar, income proof, and self-declaration of not owning a pucca house.</LI>
          <LI>The lender verifies your eligibility and submits the claim to the Central Nodal Agency (CNA) — HUDCO or NHB.</LI>
          <LI>Upon approval, the subsidy amount is directly credited to your loan account by the CNA, reducing your outstanding principal.</LI>
        </OL>
        <P>
          You can also check your eligibility and apply directly at <strong>pmaymis.gov.in</strong> or at a Common Service Centre (CSC/Jan Seva Kendra).
        </P>

        <H2>PMAY and South Indian Property Market</H2>
        <UL>
          <LI>Buyers in tier-2 cities like Coimbatore, Madurai, Tiruppur, Nellore, Vijayawada, and Mysuru — where property prices remain below the ₹45 lakh threshold for many affordable units — are well-positioned to combine PMAY-CLSS benefits with the 1% GST rate on affordable housing, significantly reducing total acquisition cost.</LI>
          <LI>The 90 sqm carpet area threshold applicable to non-metro cities in South India (most cities outside Chennai, Bengaluru, and Hyderabad) allows buyers more space under the affordable housing definition.</LI>
          <LI>Tamil Nadu's TNHB (Tamil Nadu Housing Board) and similar state housing boards often launch housing schemes that are structured to qualify under PMAY, making them especially attractive for EWS and LIG buyers.</LI>
        </UL>
      </>
    ),
  },

  // ── 6: Stamp Duty ─────────────────────────────────────────────────────────
  {
    id: '6',
    slug: 'stamp-duty-registration-charges-south-india',
    category: 'Taxation',
    audience: 'Buyers & Sellers',
    readTime: '5 min read',
    title: 'Stamp Duty and Registration Charges Across South Indian States',
    summary:
      'An approximate, state-by-state comparison of stamp duty and property registration charges in Tamil Nadu, Andhra Pradesh, Telangana, Karnataka, and Kerala — and how they are calculated.',
    icon: <AssignmentTurnedInIcon />,
    tags: ['Stamp Duty', 'Registration Charges', 'Tamil Nadu', 'Karnataka', 'Andhra Pradesh'],
    content: (
      <>
        <P>
          Stamp duty and registration charges are mandatory government levies paid at the time of property registration. They are calculated on the <strong>higher of the actual sale consideration or the guideline value</strong> (circle rate / ready reckoner value) published by each state government. These charges add significantly to the total cost of a property purchase and must be budgeted carefully.
        </P>

        <Note>
          Stamp duty rates are subject to revision by state governments, often announced in state budgets. The figures below are indicative based on publicly available information and may not reflect the latest revisions. Always verify current rates with the Sub-Registrar's office or on the state's registration department website before finalising any transaction.
        </Note>

        <H2>Approximate Stamp Duty Rates by State</H2>
        <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><strong>State</strong></TableCell>
              <TableCell><strong>Stamp Duty (approx.)</strong></TableCell>
              <TableCell><strong>Registration Charge (approx.)</strong></TableCell>
              <TableCell><strong>Other Charges</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Tamil Nadu</TableCell>
              <TableCell>7%</TableCell>
              <TableCell>1% (capped for residential)</TableCell>
              <TableCell>Surcharge applicable</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Andhra Pradesh</TableCell>
              <TableCell>5%</TableCell>
              <TableCell>1%</TableCell>
              <TableCell>Transfer duty ~1.5%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Telangana</TableCell>
              <TableCell>4%</TableCell>
              <TableCell>0.5%</TableCell>
              <TableCell>Transfer duty 1.5%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Karnataka</TableCell>
              <TableCell>3%–5% (slab-based)</TableCell>
              <TableCell>1%</TableCell>
              <TableCell>Surcharge / cess may apply</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Kerala</TableCell>
              <TableCell>~8%</TableCell>
              <TableCell>2%</TableCell>
              <TableCell>—</TableCell>
            </TableRow>
          </TableBody>
        </Table>

        <H2>Tamil Nadu — Stamp Duty Details</H2>
        <P>
          Tamil Nadu has one of the highest stamp duty rates in India. The stamp duty is calculated on the guideline value fixed by the Tamil Nadu Inspector General of Registration (TNIGR), which is revised periodically. Buyers can check the guideline value for a specific survey number on the TNIGR portal (tnreginet.gov.in) or at the Sub-Registrar's office.
        </P>
        <P>
          Women buyers in Tamil Nadu receive a concession on stamp duty — the state government periodically revises this concession, which has historically been in the range of 1–2 percentage points. Verify the current concession applicable at the time of your purchase.
        </P>

        <H2>Andhra Pradesh — Key Features</H2>
        <P>
          Andhra Pradesh charges stamp duty at 5%, plus a transfer duty of approximately 1.5%, and a 1% registration charge, making the total approximately 7.5% of the consideration value. Stamp duty values can be verified on the IGRS AP portal (igrs.ap.gov.in).
        </P>

        <H2>Telangana — Notable Difference</H2>
        <P>
          Telangana offers a relatively competitive total levy of approximately 6% (stamp duty 4% + registration 0.5% + transfer duty 1.5%), making it one of the more affordable states for registration costs in South India. Values can be checked on the IGRS Telangana portal (igrs.telangana.gov.in). Hyderabad's guideline values have been regularly revised upward given sustained real estate growth.
        </P>

        <H2>Karnataka — Slab Structure</H2>
        <P>
          Karnataka uses a slab-based stamp duty system. Buyers of lower-value properties pay a lower percentage, while properties above certain value thresholds attract a higher rate. Karnataka's registration is 1%. Buyers in Bengaluru should factor in that guideline values in prime areas like Indiranagar, Koramangala, and Whitefield can be substantial, making the absolute amount of stamp duty significant even at 5%.
        </P>

        <H2>Kerala — Highest in South India</H2>
        <P>
          Kerala has among the highest combined stamp duty and registration charges in India at approximately 8% stamp duty plus 2% registration. This significantly adds to the cost of property acquisition in Kerala. Buyers in Kerala should factor this prominently into their budget.
        </P>

        <H2>How Guideline Value (Circle Rate) Works</H2>
        <P>
          The state government fixes a minimum value per square foot / square metre for each area, called the guideline value. This is revised periodically (some states revise annually, others less frequently). If your agreed sale price is:
        </P>
        <UL>
          <LI><strong>Above the guideline value:</strong> Stamp duty is calculated on the agreed sale price.</LI>
          <LI><strong>Below the guideline value:</strong> Stamp duty is calculated on the higher guideline value, and the Sub-Registrar will register the deed at the guideline value. The income tax implications for the seller (Section 50C) also kick in — the stamp duty value is treated as the full sale consideration for capital gains purposes.</LI>
        </UL>

        <H2>Practical Tips</H2>
        <UL>
          <LI>Budget stamp duty and registration as part of your total acquisition cost from day one — these charges are paid entirely by the buyer in South India.</LI>
          <LI>Always check the guideline value of the specific survey number before negotiating the price — this sets the effective floor for registration cost.</LI>
          <LI>Stamp duty must be paid via a Demand Draft (DD) or e-stamp (depending on state) — it cannot be paid in cash for amounts above the threshold.</LI>
          <LI>Home loan disbursements are made directly to the seller; stamp duty is typically paid separately from your own funds since banks generally do not finance stamp duty (some banks include it in the loan — check with your lender).</LI>
        </UL>
      </>
    ),
  },

  // ── 7: Agricultural Land ──────────────────────────────────────────────────
  {
    id: '7',
    slug: 'agricultural-land-purchase-south-india',
    category: 'Property Types',
    audience: 'Buyers',
    readTime: '7 min read',
    title: 'Buying Agricultural and Farm Land in South India: Rules, Restrictions, and Process',
    summary:
      'State-wise guide to who can legally purchase agricultural land in Tamil Nadu, Karnataka, Andhra Pradesh, Telangana, and Kerala — including the critical restrictions on non-agriculturists that trap uninformed buyers.',
    icon: <ForestIcon />,
    tags: ['Agricultural Land', 'Farm Land', 'Land Reforms', 'Tamil Nadu', 'Karnataka'],
    content: (
      <>
        <P>
          Agricultural land purchase is one of the most legally complex property transactions in India. Each state has its own land reform legislation, and the restrictions on who can buy agricultural land vary significantly. Purchasing agricultural land without understanding these restrictions can result in the transaction being declared void and the land being resumed by the government.
        </P>

        <Note>
          Land reform laws in India are subject to amendment. The legal position on non-agriculturist purchases has changed in some states in recent years. Always obtain a legal opinion from a licensed advocate specialising in property law in the relevant state before purchasing any agricultural land.
        </Note>

        <H2>Why Agricultural Land Transactions Are Complex</H2>
        <UL>
          <LI>Most South Indian states have Land Ceiling Acts that limit how much agricultural land a family can hold.</LI>
          <LI>Several states restrict agricultural land purchase to persons classified as "agriculturists" under state law.</LI>
          <LI>Agricultural land purchased without complying with these restrictions may be confiscated by the state government.</LI>
          <LI>Converting agricultural land to non-agricultural use (for residential, industrial, or commercial purposes) requires separate government approval.</LI>
        </UL>

        <H2>Tamil Nadu</H2>
        <P>
          Tamil Nadu has strict land reform legislation under the <strong>Tamil Nadu Land Reforms (Fixation of Ceiling on Land) Act, 1961</strong>. Key points:
        </P>
        <UL>
          <LI>Generally, the transfer of agricultural land to non-agriculturists is restricted. The exact scope of restriction and permissible exemptions should be verified with a local advocate and the Tahsildar's office.</LI>
          <LI>Patta is the primary revenue document for agricultural land. The buyer must verify that the Patta is clean, in the seller's name, and free from encumbrances.</LI>
          <LI>For purchase by individuals classified as non-agriculturists, permission from the concerned authorities may be required. Failure to obtain such permission can render the sale void.</LI>
          <LI><strong>Conversion:</strong> To use agricultural land for residential or commercial purposes, the owner must apply for land use conversion (change in land classification) to the District Collector or the competent authority under the Tamil Nadu Town and Country Planning Act and Revenue Administration.</LI>
          <LI>DTCP-approved layouts carved out of agricultural land have already completed the conversion and layout approval process — plots in such layouts can be purchased freely for residential use.</LI>
        </UL>

        <H2>Karnataka</H2>
        <P>
          Karnataka had some of the most stringent restrictions under Sections 79A and 79B of the <strong>Karnataka Land Reforms Act, 1961</strong>, which historically prohibited non-agriculturists from purchasing agricultural land. These sections were amended by the <strong>Karnataka Land Reforms (Amendment) Act, 2020</strong>, which significantly liberalised the restrictions. However, the legal position has been subject to subsequent review and litigation. Buyers must verify the <strong>current prevailing law</strong> with a Karnataka-based property advocate before purchase.
        </P>
        <UL>
          <LI>RTC (Record of Rights, Tenancy and Crops) is the primary land record — verify on the Bhoomi portal (bhoomi.karnataka.gov.in).</LI>
          <LI>Agricultural land used for purposes other than agriculture without conversion permission is liable to be taken over by the state government.</LI>
          <LI>Land ceiling limits continue to apply regardless of the non-agriculturist restrictions.</LI>
        </UL>

        <H2>Andhra Pradesh</H2>
        <P>
          Andhra Pradesh is comparatively more permissive regarding non-agriculturist purchase of agricultural land, but the <strong>Andhra Pradesh Land Reforms (Ceiling on Agricultural Holdings) Act, 1973</strong> imposes ceiling limits on how much land any family can hold.
        </P>
        <UL>
          <LI>Pahani (Adangal) is the key land record — verify on the Meebhoomi portal (meebhoomi.ap.gov.in).</LI>
          <LI>Buyers should verify that the land does not form part of the government poramboke, assigned land, or ceiling surplus land — transactions involving such land are legally void.</LI>
          <LI>Conversion of agricultural land for non-agricultural use requires approval from the Collector and relevant planning authority.</LI>
        </UL>

        <H2>Telangana</H2>
        <UL>
          <LI>Telangana's agricultural land records are maintained on the <strong>Dharani portal</strong> (dharani.telangana.gov.in). Since 2020, all agricultural land registrations in Telangana must be done through Dharani.</LI>
          <LI>Buyers should verify the land's status on Dharani, confirming ownership, survey details, and that the land is not classified as prohibited (assigned land, ceiling surplus, etc.).</LI>
          <LI>Non-residents and non-agriculturists purchasing agricultural land should verify any applicable restrictions under the Telangana Land Reforms Act.</LI>
          <LI>Agricultural land in Telangana cannot be registered if it is marked as prohibited category on the Dharani portal.</LI>
        </UL>

        <H2>Kerala</H2>
        <P>
          The <strong>Kerala Land Reforms Act, 1963</strong> imposes ceiling limits on agricultural land holdings. Kerala also restricts purchase of plantations and certain agricultural lands under the Kerala Land Conservancy Act and plantation laws.
        </P>
        <UL>
          <LI>Agricultural land transactions in Kerala require verification of Thandaper (property card), Possession Certificate, and plantation records (if applicable).</LI>
          <LI>Paddy land and wetland are protected under the <strong>Kerala Conservation of Paddy Land and Wetland Act, 2008</strong> — conversion of such land is generally prohibited and transactions involving change of use can be challenged legally.</LI>
        </UL>

        <H2>Essential Due Diligence for Farm Land Purchases</H2>
        <OL>
          <LI>Confirm the survey number and exact area on government land records (Patta/RTC/Pahani).</LI>
          <LI>Verify the EC for a minimum of 13 years.</LI>
          <LI>Check that the land is not classified as poramboke, assigned land, ceiling surplus, or wetland/paddy land (as applicable).</LI>
          <LI>Confirm the applicable state law regarding non-agriculturist purchase and obtain legal opinion.</LI>
          <LI>If you intend to convert the land for non-agricultural use, understand the conversion process, approvals needed, and costs involved <strong>before</strong> purchase — not after.</LI>
          <LI>Confirm that land ceiling limits are not exceeded by your proposed purchase considering all land held by your family.</LI>
        </OL>
      </>
    ),
  },

  // ── 8: Home Loans ─────────────────────────────────────────────────────────
  {
    id: '8',
    slug: 'home-loan-guide-south-india',
    category: 'Finance',
    audience: 'Buyers',
    readTime: '7 min read',
    title: 'Home Loan Guide for South Indian Property Buyers: Eligibility, Process, and Tax Benefits',
    summary:
      'Everything you need to know about getting a home loan in India — LTV ratios, the difference between fixed and floating rates, documents required, and the income tax deductions available to borrowers.',
    icon: <ApartmentIcon />,
    tags: ['Home Loan', 'LTV Ratio', 'Tax Deduction', 'Section 80C', 'Section 24'],
    content: (
      <>
        <P>
          A home loan (housing loan) is the primary financing tool for property buyers across India. Understanding the mechanics of home loans — particularly how interest rates are structured, what the RBI mandates regarding loan-to-value ratios, and the tax benefits available — can save buyers significant amounts over the loan tenure.
        </P>

        <H2>Loan-to-Value (LTV) Ratio — RBI Guidelines</H2>
        <P>
          The Reserve Bank of India (RBI) prescribes maximum Loan-to-Value ratios for home loans, which determines how much of the property's value a bank can finance. The buyer must fund the remainder as a down payment from their own resources:
        </P>
        <Table size="small" sx={{ mb: 3, border: '1px solid', borderColor: 'divider' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell><strong>Loan Amount</strong></TableCell>
              <TableCell><strong>Maximum LTV</strong></TableCell>
              <TableCell><strong>Minimum Down Payment</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell>Up to ₹30 lakh</TableCell>
              <TableCell>90%</TableCell>
              <TableCell>10%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>₹30 lakh to ₹75 lakh</TableCell>
              <TableCell>80%</TableCell>
              <TableCell>20%</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Above ₹75 lakh</TableCell>
              <TableCell>75%</TableCell>
              <TableCell>25%</TableCell>
            </TableRow>
          </TableBody>
        </Table>
        <P>
          Note: LTV is calculated on the <strong>lower of the property's market value or registered value</strong>. Stamp duty and registration charges are generally not included in the loan amount by most lenders (though some lenders include them — verify at the time of applying).
        </P>

        <H2>Interest Rate Structure: Fixed vs Floating</H2>
        <H3>Floating Rate (Most Common)</H3>
        <P>
          Since October 2019, the RBI mandated that all new floating rate retail loans (including home loans) be linked to an external benchmark. Most banks use the <strong>Repo Rate (RBI's benchmark lending rate)</strong> or Treasury Bill rate as the external benchmark. The lending rate is expressed as:
        </P>
        <P>
          <strong>Home Loan Rate = Repo Rate + Spread</strong>
        </P>
        <P>
          The spread (bank's margin + credit risk premium) is fixed at the time of disbursement but can be revised by the bank only with borrower consent. When the RBI changes the Repo Rate, the home loan rate changes automatically and proportionately, typically within one loan reset cycle (usually quarterly).
        </P>
        <H3>Fixed Rate</H3>
        <P>
          Fixed rate loans maintain the same interest rate throughout the loan tenure (or for a specified period). They are generally priced higher than floating rates at the time of disbursement. They provide certainty of EMI but the borrower does not benefit if interest rates fall. Most lenders in India offer hybrid products: fixed for an initial period (3–5 years), then converting to floating.
        </P>

        <H2>Eligibility Criteria</H2>
        <UL>
          <LI><strong>Age:</strong> Typically 21–65 years (some lenders up to 70 for retirement-age applicants with co-borrowers).</LI>
          <LI><strong>Employment:</strong> Salaried employees with minimum 2 years of stable employment; self-employed professionals with minimum 3 years of business stability and ITR track record.</LI>
          <LI><strong>Credit score:</strong> CIBIL score of 750+ is preferred by most lenders for the best rates. Scores below 650 significantly reduce eligibility or increase rates.</LI>
          <LI><strong>Income:</strong> Net monthly income (after existing EMIs and statutory deductions) must comfortably cover the proposed EMI — most lenders target a Fixed Obligation to Income Ratio (FOIR) of 40–50%.</LI>
          <LI><strong>Property:</strong> The property must have clear title, approved plans, and must be acceptable as mortgage security to the lender. Banks will conduct a technical and legal valuation of the property before sanction.</LI>
        </UL>

        <H2>Documents Required for a Home Loan</H2>
        <H3>Identity and Income (all applicants)</H3>
        <UL>
          <LI>PAN card, Aadhaar card, passport-sized photographs.</LI>
          <LI><strong>Salaried:</strong> Latest 3 salary slips, Form 16 for last 2 years, last 6 months' bank statements, employment letter.</LI>
          <LI><strong>Self-employed:</strong> ITR for last 3 years (with computation), Profit & Loss Account and Balance Sheet, last 12 months' bank statements (personal and business), business proof (GST registration, business licence).</LI>
        </UL>
        <H3>Property Documents</H3>
        <UL>
          <LI>Sale Agreement or Allotment Letter.</LI>
          <LI>Title deed chain (past Sale Deeds).</LI>
          <LI>EC for 13 years.</LI>
          <LI>Patta / RTC / Pahani in seller's name.</LI>
          <LI>Approved layout plan / building plan sanction.</LI>
          <LI>Property tax receipt.</LI>
        </UL>

        <H2>Income Tax Deductions on Home Loans</H2>
        <H3>Section 80C — Principal Repayment</H3>
        <P>
          The principal amount repaid on a home loan during the financial year qualifies as a deduction under Section 80C of the Income Tax Act, up to a maximum of ₹1,50,000 per year. This ₹1.5 lakh limit is shared with other Section 80C investments (PPF, ELSS, NSC, life insurance premium, etc.).
        </P>
        <H3>Section 24(b) — Interest Paid</H3>
        <UL>
          <LI><strong>Self-occupied property:</strong> Deduction on interest paid up to ₹2,00,000 per year.</LI>
          <LI><strong>Let-out (rented) property:</strong> The full interest paid is deductible. However, the set-off of losses from house property against other income (like salary) is capped at ₹2,00,000 per year; the balance loss can be carried forward for 8 years.</LI>
          <LI>The property must be acquired or constructed within 5 years from the end of the financial year in which the loan was taken for the full deduction to apply.</LI>
        </UL>
        <H3>Section 80EEA — Additional Deduction for First-Time Buyers (Verify Applicability)</H3>
        <P>
          Section 80EEA provided an additional deduction of ₹1,50,000 on home loan interest for first-time homebuyers purchasing affordable housing (stamp duty value ≤ ₹45 lakh), where the loan was sanctioned between 1 April 2019 and 31 March 2022. This section has not been extended beyond 31 March 2022 as of the last budget — buyers should verify the current position for any revival of this benefit.
        </P>

        <H2>Tips for South Indian Home Loan Borrowers</H2>
        <UL>
          <LI>Compare rates across at least 4–5 lenders before deciding. Even a 0.25% difference in rate on a ₹50 lakh loan over 20 years translates to approximately ₹1.5–2 lakh in total interest savings.</LI>
          <LI>Maintain your credit score above 750. Pay all EMIs and credit card bills on time for at least 12 months before applying.</LI>
          <LI>Add a co-borrower (typically a spouse) to increase eligibility. If the co-borrower is also a co-owner, both can claim Section 24(b) deduction of up to ₹2 lakh each on their respective tax returns.</LI>
          <LI>Ensure your property has clear legal title and all approvals before applying — banks will reject or significantly delay sanctions for properties with title or approval issues.</LI>
          <LI>Pre-payment of home loans (without penalty, as mandated by RBI for floating rate loans) is one of the most effective ways to reduce total interest paid over the tenure.</LI>
        </UL>
      </>
    ),
  },

  // ── 9: Approval Bodies ────────────────────────────────────────────────────
  {
    id: '9',
    slug: 'property-approval-bodies-south-india-dtcp-cmda-hmda-bda',
    category: 'Regulations',
    audience: 'Buyers & Sellers',
    readTime: '6 min read',
    title: 'Property Approval Bodies in South India: DTCP, CMDA, HMDA, BDA, and VGTM UDA Explained',
    summary:
      'Which government authority approves layouts and buildings where you want to buy — and why buying without the right approval can be catastrophic. A state-by-state reference guide.',
    icon: <LocationCityIcon />,
    tags: ['DTCP', 'CMDA', 'HMDA', 'BDA', 'Layout Approval', 'Planning Authority'],
    content: (
      <>
        <P>
          In India, land development and construction require prior approval from the competent planning or local authority. Purchasing a plot or property without verified approvals from the relevant authority is one of the most common and costly mistakes buyers make — such properties cannot obtain bank loans, may be subject to demolition, and cannot be legally sold in the future.
        </P>

        <Note>
          Approval jurisdictions, names of bodies, and processes evolve. Verify the current competent authority for a specific location with a local advocate or directly with the relevant government department.
        </Note>

        <H2>Tamil Nadu</H2>
        <H3>CMDA — Chennai Metropolitan Development Authority</H3>
        <UL>
          <LI><strong>Jurisdiction:</strong> Chennai Metropolitan Area (CMA) — approximately 1,189 square kilometres covering Chennai city and surrounding areas including parts of Kancheepuram and Tiruvallur districts.</LI>
          <LI><strong>Role:</strong> Approves layout plans and building plans within the CMA. Any development within the CMA requires a Planning Permission from CMDA.</LI>
          <LI><strong>Key requirement:</strong> Before purchasing a plot or a building within the CMA, verify that the layout has CMDA Planning Permit approval. The CMDA permit number should be mentioned in the sale deed.</LI>
          <LI><strong>Verification:</strong> CMDA has an online portal where buyers can verify the status of approved layouts and building plans.</LI>
        </UL>
        <H3>DTCP — Directorate of Town and Country Planning, Tamil Nadu</H3>
        <UL>
          <LI><strong>Jurisdiction:</strong> All areas in Tamil Nadu <strong>outside</strong> the Chennai Metropolitan Area, and outside the limits of other Special Planning Authorities (SPAs).</LI>
          <LI><strong>Role:</strong> Approves layout plans for residential, commercial, and industrial developments in areas under its jurisdiction.</LI>
          <LI><strong>DTCP approval number</strong> must be verified in the title documents for any plot purchased in non-metro Tamil Nadu (Coimbatore, Madurai, Tiruppur, Salem, Erode, Trichy, Vellore, etc.).</LI>
          <LI>Plots in "unapproved layouts" or "panchayat layouts" without DTCP sanction carry significant legal risk — banks will not finance them.</LI>
        </UL>
        <H3>TNHB — Tamil Nadu Housing Board</H3>
        <UL>
          <LI>A government body that itself develops and sells residential plots and flats across Tamil Nadu.</LI>
          <LI>TNHB properties are government-developed — titles are typically clean and approvals are not a concern as the board is itself a planning authority.</LI>
          <LI>TNHB plots and flats are generally in high demand given the trust associated with government development.</LI>
        </UL>
        <H3>Other Tamil Nadu Authorities</H3>
        <UL>
          <LI><strong>LPA (Local Planning Authorities):</strong> Several SPAs and LPAs exist for specific cities and towns (e.g., SIPA for Sriperumbudur, specific LPAs for Salem, Madurai, Coimbatore) — always verify the applicable authority for the specific location.</LI>
        </UL>

        <H2>Telangana and Hyderabad</H2>
        <H3>HMDA — Hyderabad Metropolitan Development Authority</H3>
        <UL>
          <LI><strong>Jurisdiction:</strong> Hyderabad Metropolitan Region — a large area covering Hyderabad and surrounding districts including parts of Medchal-Malkajgiri, Ranga Reddy, Sangareddy, Vikarabad, and Yadadri Bhuvanagiri.</LI>
          <LI><strong>Role:</strong> Master planning, layout approvals, and development permissions within the metro region but outside GHMC limits.</LI>
          <LI>HMDA-approved layouts are considered premium for residential and commercial development in the outer Hyderabad region.</LI>
        </UL>
        <H3>GHMC — Greater Hyderabad Municipal Corporation</H3>
        <UL>
          <LI><strong>Jurisdiction:</strong> Hyderabad city limits (approximately 625 square kilometres).</LI>
          <LI><strong>Role:</strong> Building plan approvals and Occupancy Certificates within GHMC limits. The GHMC's DPMS (Development Permission Management System) allows online plan approvals.</LI>
        </UL>

        <H2>Andhra Pradesh</H2>
        <H3>VGTM UDA — Vijayawada Guntur Tenali Mangalagiri Urban Development Authority</H3>
        <UL>
          <LI>The primary urban development authority for the Vijayawada-Guntur corridor, which includes the Amaravati capital region.</LI>
          <LI>Layout and building approvals for this rapidly developing region fall under VGTM UDA's jurisdiction.</LI>
        </UL>
        <H3>CRDA — Capital Region Development Authority</H3>
        <UL>
          <LI>Specifically constituted for the Amaravati capital region development. Plots and layouts in CRDA jurisdiction require CRDA approval.</LI>
        </UL>
        <H3>Municipal Corporations</H3>
        <UL>
          <LI>Other major AP cities (Visakhapatnam, Tirupati, Kakinada) have their own Municipal Corporation authorities handling building plan sanctions.</LI>
        </UL>

        <H2>Karnataka</H2>
        <H3>BDA — Bangalore Development Authority</H3>
        <UL>
          <LI><strong>Jurisdiction:</strong> Bengaluru urban agglomeration area, specifically for residential layout development.</LI>
          <LI>BDA develops and allots residential sites (plots) directly to the public through periodic draws. BDA-approved layouts and allotted sites carry high legitimacy.</LI>
          <LI>BBMP (Bruhat Bengaluru Mahanagara Palike) handles building plan approvals within the city limits.</LI>
        </UL>
        <H3>BMRDA — Bangalore Metropolitan Region Development Authority</H3>
        <UL>
          <LI>Covers areas in the Bengaluru Metropolitan Region outside the BDA and BBMP jurisdiction. Layout approvals in areas like Hoskote, Devanahalli, and Ramanagara require BMRDA sanction.</LI>
        </UL>

        <H2>How to Verify Approvals</H2>
        <OL>
          <LI>Ask the seller for the original layout approval order with the approval number and the authority's name.</LI>
          <LI>Cross-verify the approval number on the respective authority's online portal or by visiting the authority's office.</LI>
          <LI>Ensure the plot you are purchasing falls <strong>within</strong> the approved layout boundary and matches the plot number in the approved plan.</LI>
          <LI>Check that the approval is not expired (some approvals have validity periods within which development must commence).</LI>
          <LI>For constructed buildings, verify that the building plan was sanctioned and that an Occupancy Certificate (OC) or Completion Certificate (CC) has been obtained.</LI>
        </OL>
      </>
    ),
  },

  // ── 10: Market Trends ────────────────────────────────────────────────────
  {
    id: '10',
    slug: 'south-india-real-estate-market-trends',
    category: 'Market Insights',
    audience: 'Buyers & Investors',
    readTime: '7 min read',
    title: 'South Indian Real Estate Market: Structural Trends and Key Investment Themes',
    summary:
      'An evidence-based overview of the structural forces reshaping South Indian real estate — manufacturing corridors, NRI demand, infrastructure development, and the growing importance of tier-2 cities.',
    icon: <TrendingUpIcon />,
    tags: ['Market Trends', 'Investment', 'Hyderabad', 'Chennai', 'Bengaluru', 'NRI'],
    content: (
      <>
        <P>
          South India's real estate market is shaped by a convergence of economic, infrastructural, and demographic forces that distinguish it meaningfully from North Indian markets. Understanding these structural trends helps buyers and investors make better-informed decisions.
        </P>

        <Note>
          Real estate is inherently local and hyper-variable. This article describes broad structural trends and does not predict specific price movements for any locality. Always conduct localised due diligence and consult market experts before investing.
        </Note>

        <H2>1. Manufacturing Corridors Driving Peripheral Land Demand</H2>
        <P>
          South India has been one of the primary beneficiaries of India's manufacturing growth, particularly in electronics, electric vehicles, and pharmaceuticals. This has created sustained demand for industrial land and associated residential development:
        </P>
        <UL>
          <LI><strong>Hosur (Tamil Nadu–Karnataka border):</strong> The Electronics Manufacturing Cluster near Hosur and the entry of multiple EV manufacturers has transformed Hosur from a small town into one of the most active manufacturing real estate markets in South India. Residential demand from factory workers and managerial staff, combined with land speculation by investors, has significantly increased activity in this corridor.</LI>
          <LI><strong>Sriperumbudur–Oragadam (Chennai):</strong> The Sriperumbudur–Oragadam industrial corridor west of Chennai, home to large automotive and electronics manufacturers, has sustained residential land demand in Kancheepuram district. Infrastructure upgrades in this corridor continue to attract both manufacturing investment and residential development.</LI>
          <LI><strong>Sri City (Andhra Pradesh):</strong> The Sri City Special Economic Zone in Chittoor district, near Chennai, has attracted diversified manufacturing and generated employment-driven residential demand in the surrounding area.</LI>
        </UL>

        <H2>2. Hyderabad — The IT Sector's Impact on Real Estate</H2>
        <P>
          Hyderabad's emergence as a major IT and ITES hub — anchored by the HITEC City, Gachibowli, Kondapur, and Nanakramguda clusters — has been one of the most significant drivers of real estate growth in South India over the past two decades. Key characteristics:
        </P>
        <UL>
          <LI>The western and northern corridors of Hyderabad have seen sustained absorption of office space by technology companies, which drives co-located residential and retail development.</LI>
          <LI>HMDA-approved outer ring road corridors (Shamshabad, Kollur, Dundigal, Ghatkesar) attract investors seeking appreciation from infrastructure-led growth.</LI>
          <LI>Hyderabad has maintained relatively lower property prices compared to Bengaluru and Mumbai for comparable quality, which continues to attract both end-user buyers and investors.</LI>
        </UL>

        <H2>3. Bengaluru — Sustained Demand with Supply Constraints</H2>
        <P>
          Bengaluru's real estate is characterised by strong fundamental demand driven by its position as India's leading IT employment hub. However, the market faces constraints:
        </P>
        <UL>
          <LI>Infrastructure bottlenecks (traffic congestion, water supply issues in outer areas) have historically moderated appreciation in some peripheral zones.</LI>
          <LI>The Namma Metro expansion has created micro-market appreciation around new stations, particularly along the Phase 2 corridors.</LI>
          <LI>Sarjapur Road, Whitefield, Electronic City, and Hebbal corridors remain the most active due to employment concentration.</LI>
          <LI>BBMP's push for OC-mandatory registrations has increased the premium on legally compliant completed properties.</LI>
        </UL>

        <H2>4. NRI Demand in South Indian Real Estate</H2>
        <P>
          The South Indian diaspora — particularly Tamilian and Telugu communities in the Gulf, UK, USA, Singapore, and Malaysia — represents a consistent and growing source of real estate investment in their home states.
        </P>
        <UL>
          <LI>NRI demand is concentrated in premium residential plots in ancestral cities (Coimbatore, Madurai, Tiruppur, Vijayawada, Visakhapatnam) and in investment land on the outskirts of major metros.</LI>
          <LI>Currency appreciation (rupee depreciation against dollar/pound/dirham) effectively reduces the real cost of Indian property for NRI buyers, historically increasing NRI demand during periods of rupee weakness.</LI>
          <LI>Remote verification tools — virtual site tours, digital documentation, and platforms like FindMyProps — have made it significantly easier for NRI buyers to transact without being physically present.</LI>
          <LI>NRI buyers must be aware of FEMA (Foreign Exchange Management Act) regulations governing property purchase in India, which permit NRI purchase of residential and commercial property but restrict purchase of agricultural/plantation land and farmhouses (which requires RBI permission).</LI>
        </UL>

        <H2>5. Tier-2 Cities: The New Growth Frontier</H2>
        <P>
          The work-from-home and hybrid-work transitions accelerated during and after 2020 have structurally increased demand for property in South India's tier-2 cities, as urban migrants return to or invest in their home towns:
        </P>
        <UL>
          <LI><strong>Coimbatore:</strong> Strong manufacturing base (textiles, engineering goods), growing IT presence, good educational infrastructure, and relatively lower property costs make Coimbatore one of the most fundamentally sound tier-2 markets in South India.</LI>
          <LI><strong>Madurai:</strong> Tamil Nadu government investment in IT parks and improved connectivity (including the ongoing expansion of Madurai airport) has attracted investment in both residential and commercial real estate.</LI>
          <LI><strong>Tiruppur:</strong> The textile export capital of India has sustained land demand driven by industrial growth and a strong business community.</LI>
          <LI><strong>Vijayawada–Guntur:</strong> As the administrative and economic hub of Andhra Pradesh, this corridor continues to attract real estate investment, particularly given the ongoing Amaravati capital development and renewed infrastructure spending.</LI>
        </UL>

        <H2>6. Infrastructure as a Real Estate Catalyst</H2>
        <P>
          Government infrastructure investment is one of the most reliable leading indicators of real estate demand in India. Key projects influencing South Indian real estate include:
        </P>
        <UL>
          <LI><strong>Expressways and National Highways:</strong> New or widened National Highway stretches consistently create land appreciation corridors on either side. The Chennai–Bengaluru Expressway, NH-44 upgrades, and the Hyderabad ORR expansions are examples.</LI>
          <LI><strong>Metro Rail:</strong> Ongoing or planned metro rail projects in Chennai (Phase 2), Bengaluru (Phase 2 and 3), Hyderabad, and Kochi create station-area micro-market appreciation.</LI>
          <LI><strong>Airport Development:</strong> Expansion of Coimbatore, Madurai, Tirupati, and Vijayawada airports has historically correlated with increased commercial and residential land activity in surrounding areas.</LI>
          <LI><strong>Industrial Corridors:</strong> The Chennai–Bengaluru Industrial Corridor and the Vizag–Chennai Industrial Corridor (part of the East Coast Economic Corridor) are long-term drivers of industrial and residential real estate demand along their routes.</LI>
        </UL>

        <H2>Key Risks to Monitor</H2>
        <UL>
          <LI><strong>Title disputes and encumbrances:</strong> South Indian agricultural land and peripheral plots carry higher title risk than urban properties — always conduct thorough due diligence.</LI>
          <LI><strong>Unapproved developments:</strong> A significant portion of construction in tier-2 cities and peripheral areas lacks proper approvals. Only purchase DTCP/CMDA/HMDA/BDA-approved properties.</LI>
          <LI><strong>Infrastructure delivery timelines:</strong> Land appreciation stories predicated on infrastructure that is delayed by years (as frequently happens in India) carry the risk of capital being locked up with no near-term appreciation catalyst.</LI>
          <LI><strong>Interest rate sensitivity:</strong> A significant portion of end-user demand is home-loan-driven. Rising interest rates reduce affordability and can moderate demand.</LI>
        </UL>
      </>
    ),
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function getArticleBySlug(slug: string): InsightArticle | undefined {
  return INSIGHT_ARTICLES.find(a => a.slug === slug);
}

export function getAllArticleMeta(): InsightArticleMeta[] {
  return INSIGHT_ARTICLES.map(({ id, slug, category, audience, readTime, title, summary, tags }) => ({
    id,
    slug,
    category,
    audience,
    readTime,
    title,
    summary,
    tags,
  }));
}

export function getArticleMetaBySlug(slug: string): InsightArticleMeta | undefined {
  return getAllArticleMeta().find(a => a.slug === slug);
}
